import React from 'react';
import { NavLink } from 'react-router-dom';

import styles from '@app/components/UI/Header/CustomMenu/CustomMenu.module.scss';
import { useMediaQuery } from '@app/hooks/useMediaQuery';
import arrowIcon from '@app/images/arrow_circle.svg';
import arrowIconActive from '@app/images/arrow_circle_active.svg';
import AngleDownSVG from '@app/images/icons/AngleDownSVG';
import { CustomMenuItem } from '@app/objects/CustomMenuItem';

interface MenuItemProps extends Omit<MenuProps, 'items'> {
	item: CustomMenuItem;
	closeMenu: () => void;
	withChildren?: boolean;
	basePath?: string;
	pathKey?: string;
	onChangeVisibleModal?: (e: string) => void;
}

interface MenuProps {
	items: Array<CustomMenuItem>;
	closeMenu: () => void;
	defaultOpen?: boolean;
	onChangeVisibleModal?: (e: string) => void;
}

const SwitchItem = ({
	item,
	switchTab,
	withIcon,
	path,
	isActive,
	exact,
	index,
	currentIndex,
}) => {
	return (
		<div
			className={`${styles.tabItem} ${currentIndex === index ? styles.tabItem__active : ''}`}
			style={{ display: 'flex' }}
		>
			<div
				onMouseEnter={() => switchTab(index)}
				className={`${styles.tabItemBtn} ${withIcon ? styles.btnText : ''}`}
			>
				{item.name}
				{item.withIcon && (
					<img
						className={`${styles.arrowIcon} ${currentIndex === index ? styles.arrowIcon__active : ''}`}
						src={currentIndex === index ? arrowIconActive : arrowIcon}
						alt="Arrow"
					/>
				)}
			</div>
			<div
				className={styles.tabItemChildren}
				style={{ justifyContent: 'space-between' }}
			>
				{item.children?.map((list, listIndex) => (
					<div
						key={listIndex}
						className={`${styles.tabItemList} ${item.name.toLowerCase()}`}
						style={{ display: 'flex', flexDirection: 'column' }}
					>
						{list.map((child, index) => {
							return child.path ? (
								!child.externalLink ? (
									<NavLink
										to={child.path}
										key={index}
										className={styles.itemLink}
									>
										{child.name}
									</NavLink>
								) : (
									<a
										key={index}
										href={child.path}
										target={child.newTab ? '_blank' : '_self'}
										rel="noreferrer noopener"
										className={styles.itemLink}
									>
										{child.name}
									</a>
								)
							) : (
								<span key={index} className={styles.itemTitle}>
									{child.name}
								</span>
							);
						})}
					</div>
				))}
			</div>
		</div>
	);
};

const Item: React.FC<MenuItemProps> = (props) => {
	const {
		item,
		withChildren,
		basePath,
		pathKey,
		closeMenu,
		onChangeVisibleModal,
	} = props;
	const [open, setOpen] = React.useState<boolean>(props.defaultOpen || item.isOpen || false);
	const isLargeScreen = useMediaQuery(1400);

	const toggleMenu = (): void => {
		setOpen((prev) => !prev);
	};

	const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

	const condition = withChildren && item.children?.length;
	const path = item[pathKey || 'path'];
	const {
		exact = false,
		isActive,
		childrenFullWidth,
		withIcon,
		externalLink,
		newTab,
		switcher,
	} = item;

	const className = `${styles.item} ${condition ? styles.itemWithChildren : ''} ${item.className || ''}`;
	const containerClassName = childrenFullWidth ? styles.itemChildrenContainerFullWidth : '';

	const [currentIndex, setCurrentIndex] = React.useState(0);

	const switchTab = (index) => {
		setCurrentIndex(index);
	};

	return (
		<li className={className} onClick={toggleMenu}>
			{path ? (
				!externalLink ? (
					<NavLink
						to={path}
						className={`${styles.itemLink} ${withIcon ? styles.btnText : ''}`}
						onClick={(e) => {
							if (item.modal && onChangeVisibleModal) {
								e.preventDefault();
								onChangeVisibleModal && onChangeVisibleModal(item.modal);
								closeMenu();
							}
						}}
					>
						{item.name}
						{withIcon && <img className={styles.arrowIcon} src={arrowIcon} alt="Arrow" />}
					</NavLink>
				) : (
					<a
						href={path}
						target={newTab ? '_blank' : '_self'}
						rel="noreferrer noopener"
						className={`${styles.itemLink} ${withIcon ? styles.btnText : ''}`}
					>
						{item.name}
						{withIcon && <img className={styles.arrowIcon} src={arrowIcon} alt="Arrow" />}
					</a>
				)
			) : (
				<span
					className={styles.itemLink}
					onClick={() => {
						if (item.modal && onChangeVisibleModal) {
							onChangeVisibleModal && onChangeVisibleModal(item.modal);
							closeMenu();
						}
					}}
				>
					{item.name}
				</span>
			)}
			{condition && !switcher && (
				<>
					<span className={styles.itemCarrot}>
						<AngleDownSVG className={`${isLargeScreen && open ? styles.carrotUp : styles.carrotDown}`} />
					</span>
					<div
						className={`${styles.itemChildrenContainer} ${open ? styles.isOpen : ''} ${containerClassName}`}
						onClick={stopPropagation}
					>
						{item.withSwitcher && !isLargeScreen ? (
							<div className={styles.tabContainer}>
								{item.children?.map((list, listIndex) => (
									<React.Fragment key={`list-${listIndex}`}>
										{list.map((child, index) => (
											<SwitchItem
												item={child}
												switchTab={switchTab}
												withIcon={withIcon}
												path={path}
												isActive={isActive}
												exact={exact}
												key={index}
												index={index}
												currentIndex={currentIndex}
											/>
										))}
									</React.Fragment>
								))}
							</div>
						) : (
							item.children?.map((list, listIndex) => (
								<ul
									className={styles.children}
									key={listIndex}
									style={{ width: '50%' }}
								>
									{list.map((child, index) => {
										if (child.modal) {
											return (
												<li
													key={index}
													className={styles.item}
												>
													<span
														className={styles.itemLink}
														onClick={(e) => {
															e.preventDefault();
															onChangeVisibleModal && onChangeVisibleModal(child.modal as string);
															closeMenu();
														}}
													>
														{child.name}
													</span>
												</li>
											);
										}

										return (
											<Item
												item={child}
												key={index}
												withChildren={withChildren}
												pathKey={pathKey}
												closeMenu={closeMenu}
											/>
										);
									})}
								</ul>
							))
						)}
					</div>
				</>
			)}
		</li>
	);
};

const CustomMenu: React.FC<MenuProps> = ({
	items,
	defaultOpen,
	closeMenu,
	onChangeVisibleModal,
}) => {
	const menuItems = items || [];

	return (
		<ul className={styles.menuComponent}>
			{menuItems.map((item, index) => (
				<Item
					defaultOpen={defaultOpen}
					key={index}
					item={item}
					withChildren={!!item.children}
					closeMenu={closeMenu}
					onChangeVisibleModal={onChangeVisibleModal && onChangeVisibleModal}
				/>
			))}
		</ul>
	);
};

export default CustomMenu;
